import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 24, className = "", as = "div", ...rest }) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      initial={{ opacity: 0, y: reduce ? 0 : y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

const splitOnBreaks = (children) => {
  const lines = [[]];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === "br") {
      lines.push([]);
    } else {
      lines[lines.length - 1].push(child);
    }
  });
  return lines;
};

export const MaskedLines = ({ children, delay = 0, stagger = 0.12, className = "" }) => {
  const reduce = useReducedMotion();
  const lines = splitOnBreaks(children);
  if (reduce) return <span className={className}>{children}</span>;
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <motion.span
          key={i}
          className="-mb-[0.1em] block overflow-hidden pb-[0.1em] pr-[0.08em]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px 0px" }}
        >
          <motion.span
            className="block"
            variants={{
              hidden: { y: "112%" },
              visible: {
                y: 0,
                transition: {
                  duration: 0.95,
                  ease: EASE,
                  delay: delay + i * stagger,
                },
              },
            }}
          >
            {line}
          </motion.span>
        </motion.span>
      ))}
    </span>
  );
};


export const SectionTag = ({ children, light = false, index }) => (
  <p
    className={`flex items-baseline text-xs font-medium uppercase tracking-[0.3em] ${
      light ? "text-stone-400" : "text-slate-300"
    }`}
  >
    {index && (
      <span
        className={`mr-4 font-serif text-sm normal-case tracking-normal ${
          light ? "text-[#A8853B]" : "text-[#D4AF37]/80"
        }`}
      >
        {index}
      </span>
    )}
    {children}
  </p>
);

export const Hairline = ({ light = false, className = "" }) => (
  <div
    className={`h-px w-24 ${
      light
        ? "bg-gradient-to-r from-[#D4AF37]/40 to-transparent"
        : "bg-gradient-to-r from-slate-500/60 to-transparent"
    } ${className}`}
  />
);

export const ArrowLink = ({ to, children, light = false, testid }) => {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testid}
      onClick={() => navigate(to)}
      className={`group mt-10 inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 ${
        light ? "text-slate-200 hover:text-[#A8853B]" : "text-slate-200 hover:text-[#E7CE8B]"
      }`}
    >
      <span className="border-b border-current pb-1">{children}</span>
      <span className="transition-transform duration-500 group-hover:translate-x-1.5">→</span>
    </button>
  );
};
